import { DatePicker, Popover } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react'
import styled from 'styled-components';
import { IsMobile, IsTablet } from '../../../Utils/UtilityFunctions';
import { DATE_RANGES } from '../../../Utils/Constants';
import dayjs, { Dayjs } from 'dayjs';
import { primaryColor, greyColor, primaryFont, wMedium, maxWidth, retina, maxTablet } from '../../../Utils/utilsStyle';
import { selectUserCreationDate } from '../../../Selector/GlobalSearchContentSelector';
import { useSelector } from 'react-redux';
import { DateRangeValue } from '../../../Types/GlobalTypes';
const { RangePicker } = DatePicker;
const Wrapper = styled.div`
  .date-filter-btn {
      min-width: 140px;
      border-radius: 8px;
      border: 0.711px solid #DADADA;
      background: #FFF;
      padding: 9px 16px;
      line-height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      &.active {
        background: rgb(222, 246, 230);
        .text {
          color: ${primaryColor};
        }
        .icon {
          svg {
            fill: ${primaryColor};
          }
        }
      }
      &.disabled {
        opacity: 0.5;
        pointer-events: none;
      }
      .text {
        color: ${greyColor};
        font-family: ${primaryFont};
        font-size: 16px;
        text-align: center;
        font-weight: ${wMedium};
        line-height: 20px;
        letter-spacing: -0.4px;
        @media all and (max-width: ${maxWidth}), ${retina} {
          font-size: 14px;
          line-height: 18px;
        }
      }
      .icon {
        svg {
          width: 20px;
          height: 20px;
          fill: ${greyColor};
        }
      }
      @media all and (max-width: ${maxWidth}), ${retina} {
        padding: 6px 16px;
        border-radius: 6px;
      }
      @media all and (max-width: ${maxTablet}) {
        min-width: 50px;
        padding: 2px 12px;
      }
    }
`;

type Props = {
  defaultRange: string;
  startDate?: string | null;
  endDate?: string | null;
  onChange: (value: DateRangeValue) => void;
  disabled?: boolean;
};

const defaultProps = {
  defaultRange: 'Last 3 Months',
  onChange: () => { },
  disabled: false,
}
const DateRangeFilter = function DateRangeFilter(props: Props) {
  const { defaultRange, startDate, endDate, onChange, disabled } = props;
  const [open, setOpen] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);
  const [selectedRange, setSelectedRange] = useState<RangeType>(defaultRange);
  const [lastSelectedRange, setLastSelectedRange] = useState<RangeType>(defaultRange);
  const [customRange, setCustomRange] = useState<[Dayjs | null, Dayjs | null] | null>(() => (
    defaultRange === 'Custom Range' && startDate && endDate
      ? [dayjs(startDate), dayjs(endDate)]
      : null
  ));
  const isTab = IsTablet();
  type RangeType = typeof DATE_RANGES[number];
  const userCreationDate = useSelector(selectUserCreationDate);
  const isMobile = IsMobile();

  useEffect(() => {
    setSelectedRange(defaultRange as RangeType);
    setLastSelectedRange(defaultRange as RangeType);
    setCustomRange(
      defaultRange === 'Custom Range' && startDate && endDate
        ? [dayjs(startDate), dayjs(endDate)]
        : null,
    );
  }, [defaultRange, startDate, endDate]);

  const setRangeDates = (range: RangeType): { start: Dayjs; end: Dayjs; allTime?: boolean } | null => {
    const today = dayjs().endOf('day');

    switch (range) {
      case 'Last 2 Days':
        return {
          start: today.subtract(1, 'day').startOf('day'),
          end: today,
        };
      case 'Last 7 Days':
        return {
          start: today.subtract(6, 'day').startOf('day'),
          end: today,
        };
      case 'Last 2 Weeks':
        return {
          start: today.subtract(13, 'day').startOf('day'),
          end: today,
        };
      case 'Last 1 Month':
        return {
          start: today.subtract(29, 'day').startOf('day'),
          end: today,
        };
      case 'Last 3 Months':
        return {
          start: today.subtract(89, 'day').startOf('day'),
          end: today,
        };
      case 'All Time':
        return {
          start: dayjs(userCreationDate),
          end: today,
          allTime: true,
        };
      default:
        return null;
    }
  };

  const selectRangeHandler = (range: RangeType) => {
    setSelectedRange(range);
    setLastSelectedRange(range);
    setActive(true);

    if (range === 'Custom Range') return;
    setCustomRange(null);
    setOpen(false);

    const dates = setRangeDates(range);
    if (!dates) return;

    onChange({
      type: range,
      startDate: dates.start.format('YYYY-MM-DD HH:mm:ss'),
      endDate: dates.end.format('YYYY-MM-DD HH:mm:ss'),
      allTime: dates.allTime ?? false,
    })
  };

  const handleCustomRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates?.[0] || !dates?.[1]) return;

    const selectedDates: [Dayjs, Dayjs] = [dates[0], dates[1]];
    setSelectedRange('Custom Range');
    setCustomRange(selectedDates);
    setLastSelectedRange('Custom Range');
    setOpen(false);

    onChange({
      type: 'Custom Range',
      startDate: selectedDates[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      endDate: selectedDates[1].endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      allTime: false,
    });

  }

  const content = (
    <div className='filter-dropdown'>
      {DATE_RANGES.map((range) => (
        <div
          className={classNames('item', {
            'active': customRange?.[0] && customRange?.[1] ? range === selectedRange : range === lastSelectedRange
          })}
          key={range}
          onClick={() => selectRangeHandler(range)}
        >
          {range}
        </div>
      ))}
      {
        selectedRange === 'Custom Range' && (
          <div className='custom-range'>
            <RangePicker
              popupClassName='custom-range-popup'
              value={customRange}
              format='YYYY-MM-DD'
              disabledDate={(current) => current && (current.isAfter(dayjs(), 'day'))}
              // 
              onChange={handleCustomRangeChange}
              getPopupContainer={() => document.body}
            />
          </div>
        )
      }

    </div>
  );

  const customStartDate = customRange?.[0];
  const customEndDate = customRange?.[1];
  const displayLabel = customStartDate && customEndDate
    ? isTab
      ? `${customStartDate.format('DD/MM/YY')} -> ${customEndDate.format('DD/MM/YY')}`
      : `${customStartDate.format('MMMM D, YYYY')} -> ${customEndDate.format('MMMM D, YYYY')}`
    : selectedRange === 'Custom Range'
      ? lastSelectedRange
      : selectedRange;
  return (
    <Wrapper>
      <Popover
        open={open}
        onOpenChange={(newOpen) => setOpen(newOpen)}
        trigger="click"
        placement='bottomLeft'
        getPopupContainer={() => document.body}
        content={content}
        zIndex={10000}
        classNames={{ root: "custom-date-popover" }}
      >
        <div
          role='button'
          aria-label="custom-date-filter"
          className={classNames('date-filter-btn', { active: active, disabled: disabled })}
        >
          <span className='text'>{displayLabel}</span>
        </div>

      </Popover>
    </Wrapper>
  );
}

DateRangeFilter.defaultProps = defaultProps;
export default DateRangeFilter;